// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Authentication from '@app/features/auth/state/Authentication';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {http} from '@app/features/platform/transport/RestTransport';
import type {RestResponse} from '@app/features/platform/types/TransportTypes';
import {reaction} from 'mobx';

// Micup akis-izlenme. A card at least half on screen (or covering half the screen) for a second is a view, however long
// it stays; a post the server took is not sent again from this device for 24 h, the server counts it once a viewer a
// day. Views go to the server in one batch a minute, the most it takes from an account. The server trusts only the
// post's view_token.
export const FEED_VIEW_SEND_MS = 60_000;
export const FEED_VIEW_DWELL_MS = 1000;
const FEED_VIEW_REMEMBER_MS = 24 * 3_600_000;
// The posts remembered across reloads, oldest dropped first: about 60 KB of localStorage.
const FEED_VIEW_REMEMBER_MAX = 2000;
const rememberedKey = (userId: string) => `micup:feed:views-sent:${userId}`;
// The server clamps a count to the same.
const FEED_VIEW_MAX_COUNT = 1;
// The server's gate is 60 s from the batch's arrival; waiting 61–62 s from its answer never meets a closed gate.
const FEED_VIEW_GAP_MS = 61_000;
const FEED_VIEW_GAP_JITTER_MS = 1000;
const FEED_VIEWS_PER_REQUEST = 100; // FeedViewsRequest on the server
// A send with no answer by then (a half-open connection) fails like a network error instead of holding back the rest.
const FEED_VIEW_TIMEOUT_MS = 30_000;
// A 403 is view counts off (the emergency brake): no send, not even on exit, for this long; a new account asks at once.
// TEMPORARY: while they are off an open tab still asks once in 30 min (10 M tabs ≈ 5.6 K 403/s through the rate
// limiter); stop until the page reloads if that load matters.
const FEED_VIEW_OFF_MS = 30 * 60_000;
// 0.5 for an ordinary card; the lower steps let a card taller than twice the screen count once it covers half of it.
// TEMPORARY: a card over ten screens tall never counts.
export const FEED_VIEW_THRESHOLDS = [0.1, 0.2, 0.3, 0.4, 0.5];

export function isHalfVisible(entry: IntersectionObserverEntry): boolean {
	if (!entry.isIntersecting) return false;
	if (entry.intersectionRatio >= 0.5) return true;
	return entry.rootBounds !== null && entry.intersectionRect.height >= entry.rootBounds.height / 2;
}

type ViewBatch = Array<{id: string; token: string; count: number}>;
// A 429's body (RateLimitError); other answers carry nothing read here.
type RateLimitBody = {retry_after?: number} | undefined;

const pending = new Map<string, {token: string; count: number}>(); // post id → view_token and views not sent yet
const onScreen = new Map<string, {token: string; since: number}>(); // cards half on screen now, since when
const parked = new Map<string, string>(); // post id → view_token, cards on screen while the page is hidden
const inFlight = new Set<string>(); // posts of a batch sent and not answered yet
let remembered: Map<string, number> | null = null; // post id → when the server took its view, oldest first
let rememberedFor: string | null = null;
let nextSendAt = 0; // the earliest time the server's minute gate is open again, as far as this tab knows
let sending = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let generation = 0;

const gap = () => FEED_VIEW_GAP_MS + Math.random() * FEED_VIEW_GAP_JITTER_MS;

// Keeps a timer already set: after a 429 or a failure it is the one that counts.
function scheduleAt(at: number): void {
	if (timer !== null) return;
	timer = setTimeout(
		() => {
			timer = null;
			void sendViews();
		},
		Math.max(0, at - Date.now()),
	);
}

function cancelTimer(): void {
	if (timer !== null) clearTimeout(timer);
	timer = null;
}

// Read once per account, written with each batch the server took; storage that throws (private mode, quota) only
// loses the memory.
function rememberedViews(now: number): Map<string, number> {
	const userId = Authentication.currentUserId ?? '';
	if (remembered !== null && rememberedFor === userId) return remembered;
	remembered = new Map();
	rememberedFor = userId;
	try {
		const stored: unknown = JSON.parse(localStorage.getItem(rememberedKey(userId)) ?? '[]');
		for (const entry of Array.isArray(stored) ? stored : []) {
			if (!Array.isArray(entry)) continue;
			const [id, at] = entry as [unknown, unknown];
			if (typeof id === 'string' && typeof at === 'number' && now - at < FEED_VIEW_REMEMBER_MS) remembered.set(id, at);
		}
	} catch {}
	return remembered;
}

function saveRemembered(): void {
	if (remembered === null || rememberedFor === null) return;
	try {
		localStorage.setItem(rememberedKey(rememberedFor), JSON.stringify([...remembered]));
	} catch {}
}

function forgetRemembered(): void {
	try {
		localStorage.removeItem(rememberedKey(Authentication.currentUserId ?? ''));
	} catch {}
}

function wasSent(postId: string, now: number): boolean {
	const at = rememberedViews(now).get(postId);
	return at !== undefined && now - at < FEED_VIEW_REMEMBER_MS;
}

function isTaken(postId: string, now: number): boolean {
	return pending.has(postId) || inFlight.has(postId) || wasSent(postId, now);
}

function takeBatch(): ViewBatch {
	const batch = Array.from(pending, ([id, {token, count}]) => ({id, token, count}));
	pending.clear();
	for (const {id} of batch) inFlight.add(id);
	return batch;
}

// The batch is answered; true when the server took it, and its posts are not sent again for 24 h.
function settle(batch: ViewBatch, took: boolean): void {
	for (const {id} of batch) inFlight.delete(id);
	if (!took) return;
	const now = Date.now();
	const sent = rememberedViews(now);
	for (const {id} of batch) {
		sent.delete(id);
		sent.set(id, now);
	}
	while (sent.size > FEED_VIEW_REMEMBER_MAX) sent.delete(sent.keys().next().value!);
	saveRemembered();
}

// Views past the 100 posts a request holds are dropped; a post holds at most 1 a batch.
function addViews(postId: string, token: string, views: number): boolean {
	const count = pending.get(postId)?.count;
	if (count === undefined && pending.size >= FEED_VIEWS_PER_REQUEST) return false;
	pending.set(postId, {token, count: Math.min(FEED_VIEW_MAX_COUNT, (count ?? 0) + views)});
	return true;
}

// The post's one view in 24 h; true when it was queued.
function takeView(postId: string, token: string, now: number): boolean {
	return !isTaken(postId, now) && addViews(postId, token, 1);
}

// Cards that have stayed on screen for the second, taken without waiting for them to leave.
function takeStayingViews(now: number): void {
	for (const [postId, {token, since}] of onScreen) {
		if (now - since >= FEED_VIEW_DWELL_MS) takeView(postId, token, now);
	}
}

function hasViewOnScreen(now: number): boolean {
	for (const postId of onScreen.keys()) if (!isTaken(postId, now)) return true;
	return false;
}

// A batch the server did not take goes back, within the 100 a request holds; a view recorded since keeps its newer token.
function putBack(batch: ViewBatch): void {
	for (const {id, token, count} of batch) addViews(id, pending.get(id)?.token ?? token, count);
}

function scheduleSend(now: number): void {
	if (!sending) scheduleAt(Math.max(now + FEED_VIEW_SEND_MS, nextSendAt));
}

/** The card is half on screen; a later crossing while it stays only renews its token. */
export function recordView(postId: string, token: string, now = Date.now()): void {
	if (AccountScopedWork.isSuspended) return;
	const current = onScreen.get(postId);
	if (current !== undefined) {
		current.token = token;
		return;
	}
	onScreen.set(postId, {token, since: now});
	if (!isTaken(postId, now)) scheduleSend(now);
}

/** The card is no longer half on screen, or is gone: a view if it stayed a second. */
export function endView(postId: string, now = Date.now()): void {
	const entry = onScreen.get(postId);
	onScreen.delete(postId);
	parked.delete(postId);
	if (entry !== undefined && now - entry.since >= FEED_VIEW_DWELL_MS && takeView(postId, entry.token, now)) {
		scheduleSend(now);
	}
}

export async function sendViews(): Promise<void> {
	if (AccountScopedWork.isSuspended || sending) return;
	const now = Date.now();
	takeStayingViews(now);
	if (pending.size === 0) {
		// A card that came on screen less than a second ago.
		if (hasViewOnScreen(now)) scheduleSend(now);
		return;
	}
	const requestGeneration = generation;
	sending = true;
	const userId = Authentication.currentUserId;
	const batch = takeBatch();
	let response: RestResponse<RateLimitBody> | null = null;
	try {
		response = await http.post<RateLimitBody>(Endpoints.FEED_VIEWS, {
			body: {views: batch},
			timeoutMs: FEED_VIEW_TIMEOUT_MS,
			retries: 0,
			mode: 'silent',
		});
	} catch {
		// A network error or a timeout: with no retries http.post rejects once, and the batch waits a minute like after a 5xx.
	}
	if (requestGeneration !== generation) return;
	sending = false;
	settle(batch, Authentication.currentUserId === userId && response?.ok === true);
	if (Authentication.currentUserId !== userId) {
		// The previous account's answer: nothing of it carries over.
	} else if (response === null || response.status >= 500) {
		putBack(batch);
		scheduleAt(Date.now() + FEED_VIEW_SEND_MS);
	} else if (response.status === 429) {
		putBack(batch);
		nextSendAt = Date.now() + (response.body?.retry_after ?? FEED_VIEW_SEND_MS / 1000) * 1000 + 1000;
		scheduleAt(nextSendAt);
	} else if (response.status === 403) {
		pending.clear();
		nextSendAt = Date.now() + FEED_VIEW_OFF_MS;
	} else if (response.ok) {
		nextSendAt = Date.now() + gap();
	}
	// Any other answer (400, 401, 404: a bad batch, signed out, a server before view counts) drops it.
	if (pending.size > 0 || hasViewOnScreen(Date.now())) scheduleAt(Math.max(Date.now() + FEED_VIEW_SEND_MS, nextSendAt));
}

// On leaving the page. sendBeacon cannot carry the Authorization header; a keepalive fetch outlives the page like it.
export function sendViewsOnExit(now = Date.now()): void {
	if (AccountScopedWork.isSuspended) return;
	takeStayingViews(now);
	// The server would refuse it: a batch is on its way, or its minute gate is still closed.
	if (sending || pending.size === 0 || now < nextSendAt) return;
	const token = Authentication.authToken;
	const url = new URL(
		`${RuntimeConfig.apiEndpoint}/v${RuntimeConfig.apiCodeVersion}${Endpoints.FEED_VIEWS}`,
		location.origin,
	);
	// As in RestTransport: the session token goes to the page's own origin only.
	if (!token || url.origin !== location.origin) return;
	const batch = takeBatch();
	const exitGeneration = generation;
	cancelTimer();
	nextSendAt = now + gap();
	void fetch(url, {
		method: 'POST',
		keepalive: true,
		headers: {'Content-Type': 'application/json', Authorization: token},
		body: JSON.stringify({views: batch}),
	})
		.then(
			(response) => response.ok,
			() => false,
		)
		.then((took) => {
			if (exitGeneration === generation) settle(batch, took);
		});
}

// A hidden page's cards are set aside until it shows again, and are back on screen without a view for coming back.
function pauseOnScreen(now = Date.now()): void {
	takeStayingViews(now);
	for (const [postId, {token}] of onScreen) parked.set(postId, token);
	onScreen.clear();
}

function resumeOnScreen(now = Date.now()): void {
	if (AccountScopedWork.isSuspended) return;
	for (const [postId, token] of parked) onScreen.set(postId, {token, since: now});
	parked.clear();
}

export function resetViews(): void {
	generation += 1;
	sending = false;
	pending.clear();
	onScreen.clear();
	parked.clear();
	inFlight.clear();
	remembered = null;
	rememberedFor = null;
	nextSendAt = 0;
	cancelTimer();
}

// Another account on this device starts with nothing of the previous one's views; signing out or switching leaves none
// of them in this device's storage.
reaction(() => Authentication.currentUserId, resetViews);
AccountScopedWork.registerCancellation(() => {
	forgetRemembered();
	resetViews();
});

if (typeof window !== 'undefined') {
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState !== 'hidden') {
			resumeOnScreen();
			return;
		}
		pauseOnScreen();
		sendViewsOnExit();
	});
	window.addEventListener('pagehide', () => sendViewsOnExit());
}
